/**
 * COMPANY REGISTRATION FORM
 *
 * Multi-step form for registering a new company account.
 * Collects company details, addresses, and initial officers.
 */

import React, { useState, useCallback, useMemo, memo } from 'react';
import { CompanyAccountService } from '../../lib/services/companyAccountService';
import { sendRegistrationConfirmationEmail } from '../../lib/services/companyEmailService';
import { isSouthAfricaCompanyCountry, validateCompanyRegistrationNumber } from '../../lib/companyRegistrationValidation.js';
import type { CreateCompanyAccountInput, Address, CompanyOfficerRole } from '../../types/company';
import { validateNameField, validateStrictEmail, validatePhoneField, validateAlphanumericField, FIELD_LIMITS } from '../../lib/userInputValidation';
import { useSupportedCountries } from '../../lib/hooks/useSupportedCountries';

// Memoized Input Field Component - defined OUTSIDE the main component to prevent re-creation
interface InputFieldProps {
  label: string;
  field: string;
  value: string;
  onChange: (field: string, value: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  error?: string;
  maxLength?: number;
}

const InputField = memo(({
  label,
  field,
  value,
  onChange,
  type = 'text',
  required = false,
  placeholder = '',
  error,
  maxLength
}: InputFieldProps) => {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-300">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`w-full px-4 py-2 bg-gray-700 border ${
          error ? 'border-red-500' : 'border-gray-600'
        } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500`}
      />
      {error && <p className="text-red-400 text-sm">{error}</p>}
    </div>
  );
});

InputField.displayName = 'InputField';

// Memoized Select Field Component
interface SelectFieldProps {
  label: string;
  field: string;
  value: string;
  onChange: (field: string, value: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  error?: string;
}

const SelectField = memo(({
  label,
  field,
  value,
  onChange,
  options,
  required = false,
  error
}: SelectFieldProps) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-gray-300">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <select
      value={value || ''}
      onChange={(e) => onChange(field, e.target.value)}
      className={`w-full px-4 py-2 bg-gray-700 border ${
        error ? 'border-red-500' : 'border-gray-600'
      } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
    >
      <option value="">Select...</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {error && <p className="text-red-400 text-sm">{error}</p>}
  </div>
));

SelectField.displayName = 'SelectField';

// Additional officer to add during registration
interface AdditionalOfficer {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: CompanyOfficerRole;
  customRoleTitle?: string;
  shareholdingPercentage: number;
}

const officerRoles: { value: CompanyOfficerRole; label: string }[] = [
  { value: 'director', label: 'Director' },
  { value: 'non_executive_director', label: 'Non-Executive Director' },
  { value: 'ceo', label: 'CEO' },
  { value: 'cfo', label: 'CFO' },
  { value: 'cto', label: 'CTO' },
  { value: 'coo', label: 'COO' },
  { value: 'chairman', label: 'Chairman' },
  { value: 'company_secretary', label: 'Company Secretary' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'shareholder_representative', label: 'Shareholder Representative' },
  { value: 'authorized_signatory', label: 'Authorized Signatory' },
  { value: 'custom', label: 'Other' }
];

const emptyOfficer: AdditionalOfficer = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: 'director',
  shareholdingPercentage: 0
};

interface CompanyRegistrationFormProps {
  userId: number;
  onSuccess: (companyId: string) => void;
  onCancel: () => void;
}

type FormStep = 'company-info' | 'addresses' | 'contact' | 'officers' | 'review';

// South African company types
const companyTypes = [
  { value: 'pty_ltd', label: 'Private Company (Pty) Ltd' },
  { value: 'ltd', label: 'Public Company Ltd' },
  { value: 'npc', label: 'Non-Profit Company (NPC)' },
  { value: 'inc', label: 'Incorporated Company' },
  { value: 'cc', label: 'Close Corporation (CC)' },
  { value: 'trust', label: 'Trust' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'sole_proprietor', label: 'Sole Proprietorship' },
  { value: 'other', label: 'Other' }
];

// Industry classifications
const industries = [
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'technology', label: 'Technology & IT' },
  { value: 'mining', label: 'Mining & Resources' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail & Commerce' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'real_estate', label: 'Real Estate & Property' },
  { value: 'construction', label: 'Construction' },
  { value: 'transport', label: 'Transport & Logistics' },
  { value: 'hospitality', label: 'Hospitality & Tourism' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'other', label: 'Other' }
];

const initialFormData: CreateCompanyAccountInput = {
  legalName: '',
  tradingName: '',
  registrationNumber: '',
  taxIdentificationNumber: '',
  jurisdictionCountryCode: 'ZAF',
  companyType: '',
  dateOfIncorporation: '',
  industryClassification: '',
  industryName: '',
  businessDescription: '',
  registeredAddress: {
    street: '',
    city: '',
    province: '',
    postalCode: '',
    countryCode: 'ZAF'
  },
  businessAddress: {
    street: '',
    city: '',
    province: '',
    postalCode: '',
    countryCode: 'ZAF'
  },
  primaryEmail: '',
  primaryPhone: '',
  websiteUrl: ''
};

export const CompanyRegistrationForm: React.FC<CompanyRegistrationFormProps> = ({
  userId,
  onSuccess,
  onCancel
}) => {
  const { countryOptions } = useSupportedCountries();
  const companyCountryOptions = useMemo(
    () => [...countryOptions].sort((first, second) => first.label.localeCompare(second.label)),
    [countryOptions]
  );
  const [currentStep, setCurrentStep] = useState<FormStep>('company-info');
  const [formData, setFormData] = useState<CreateCompanyAccountInput>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [useBusinessAddress, setUseBusinessAddress] = useState(false);
  const [additionalOfficers, setAdditionalOfficers] = useState<AdditionalOfficer[]>([]);
  const [showAddOfficer, setShowAddOfficer] = useState(false);
  const [newOfficer, setNewOfficer] = useState<AdditionalOfficer>({ ...emptyOfficer });

  const steps: { id: FormStep; label: string }[] = [
    { id: 'company-info', label: 'Company Info' },
    { id: 'addresses', label: 'Addresses' },
    { id: 'contact', label: 'Contact' },
    { id: 'officers', label: 'Officers' },
    { id: 'review', label: 'Review' }
  ];

  // Use useCallback to prevent unnecessary re-renders
  const updateField = useCallback((field: string, value: string) => {
    setFormData(prev => {
      const keys = field.split('.');
      if (keys.length === 1) {
        if (field === 'jurisdictionCountryCode') {
          return {
            ...prev,
            jurisdictionCountryCode: value,
            registeredAddress: {
              ...prev.registeredAddress,
              countryCode: prev.registeredAddress.countryCode && prev.registeredAddress.countryCode !== prev.jurisdictionCountryCode
                ? prev.registeredAddress.countryCode
                : value
            },
            businessAddress: prev.businessAddress ? {
              ...prev.businessAddress,
              countryCode: prev.businessAddress.countryCode && prev.businessAddress.countryCode !== prev.jurisdictionCountryCode
                ? prev.businessAddress.countryCode
                : value
            } : prev.businessAddress
          };
        }
        return { ...prev, [field]: value };
      }
      // Handle nested fields like registeredAddress.street
      const [parent, child] = keys;
      return {
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value
        }
      };
    });
    // Clear error for this field
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });
  }, []);

  // Helper function to get field value (handles nested fields)
  const getFieldValue = useCallback((field: string): string => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      return (formData as any)[parent]?.[child] || '';
    }
    return (formData as any)[field] || '';
  }, [formData]);

  // Validation helper functions
  const validateTaxNumber = (taxNum: string): boolean => {
    // South African tax number format: 10 digits
    // Also accept empty (optional field)
    if (!taxNum || !taxNum.trim()) return true;
    const saFormat = /^\d{10}$/;
    const genericFormat = /^[A-Z0-9]{5,20}$/i;
    return saFormat.test(taxNum.replace(/[\s-]/g, '')) || genericFormat.test(taxNum);
  };

  const __validatePhoneNumber = (phone: string): boolean => {
    // Accept international formats with + prefix or local formats
    if (!phone || !phone.trim()) return true;
    const cleanPhone = phone.replace(/[\s()-]/g, '');
    // International: +27XXXXXXXXX or local: 0XXXXXXXXX (9-15 digits)
    const pattern = /^(\+\d{1,3})?\d{9,15}$/;
    return pattern.test(cleanPhone);
  };

  const validatePostalCode = (postalCode: string, country: string): boolean => {
    if (!postalCode) return false;
    // South African postal codes: 4 digits
    if (country === 'ZA') {
      return /^\d{4}$/.test(postalCode.trim());
    }
    // Generic: 3-10 alphanumeric characters
    return /^[A-Z0-9]{3,10}$/i.test(postalCode.trim());
  };

  const validateStep = (step: FormStep): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 'company-info':
        if (!formData.legalName.trim()) {
          newErrors.legalName = 'Legal name is required';
        } else if (formData.legalName.trim().length < 2) {
          newErrors.legalName = 'Legal name must be at least 2 characters';
        }

        const registrationValidation = validateCompanyRegistrationNumber(
          formData.registrationNumber,
          formData.jurisdictionCountryCode || formData.registeredAddress.countryCode || 'ZAF'
        );
        if (!registrationValidation.valid) {
          newErrors.registrationNumber = registrationValidation.error || 'Invalid company registration number';
        }

        if (!formData.companyType) {
          newErrors.companyType = 'Company type is required';
        }

        if (!formData.dateOfIncorporation) {
          newErrors.dateOfIncorporation = 'Date of incorporation is required';
        } else {
          const incDate = new Date(formData.dateOfIncorporation);
          const today = new Date();
          if (incDate > today) {
            newErrors.dateOfIncorporation = 'Date of incorporation cannot be in the future';
          }
        }

        // Optional tax number validation
        if (formData.taxIdentificationNumber && !validateTaxNumber(formData.taxIdentificationNumber)) {
          newErrors.taxIdentificationNumber = 'Invalid tax number format. SA format: 10 digits';
        }
        break;

      case 'addresses': {
        const streetResult = validateAlphanumericField(formData.registeredAddress.street, 'Street address');
        if (!streetResult.valid) {
          newErrors['registeredAddress.street'] = streetResult.error!;
        }
        const cityResult = validateAlphanumericField(formData.registeredAddress.city, 'City');
        if (!cityResult.valid) {
          newErrors['registeredAddress.city'] = cityResult.error!;
        }
        if (formData.registeredAddress.province?.trim()) {
          const provResult = validateAlphanumericField(formData.registeredAddress.province || '', 'Province/State', { optional: true });
          if (!provResult.valid) {
            newErrors['registeredAddress.province'] = provResult.error!;
          }
        }
        if (!formData.registeredAddress.postalCode.trim()) {
          newErrors['registeredAddress.postalCode'] = 'Postal code is required';
        } else if (!validatePostalCode(formData.registeredAddress.postalCode, formData.jurisdictionCountryCode || 'ZA')) {
          newErrors['registeredAddress.postalCode'] = 'Invalid postal code format';
        }
        break;
      }

      case 'contact': {
        const emailResult = validateStrictEmail(formData.primaryEmail);
        if (!emailResult.valid) {
          newErrors.primaryEmail = emailResult.error!;
        }

        // Phone validation (optional but if provided must be valid)
        if (formData.primaryPhone && formData.primaryPhone.trim()) {
          const phoneResult = validatePhoneField(formData.primaryPhone);
          if (!phoneResult.valid) {
            newErrors.primaryPhone = phoneResult.error!;
          }
        }
        break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      const stepIndex = steps.findIndex(s => s.id === currentStep);
      if (stepIndex < steps.length - 1) {
        setCurrentStep(steps[stepIndex + 1].id);
      }
    }
  };

  const handleBack = () => {
    const stepIndex = steps.findIndex(s => s.id === currentStep);
    if (stepIndex > 0) {
      setCurrentStep(steps[stepIndex - 1].id);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const registrationValidation = validateCompanyRegistrationNumber(
        formData.registrationNumber,
        formData.jurisdictionCountryCode || formData.registeredAddress.countryCode || 'ZAF'
      );
      if (!registrationValidation.valid) {
        setErrors(prev => ({
          ...prev,
          registrationNumber: registrationValidation.error || 'Invalid company registration number'
        }));
        setCurrentStep('company-info');
        return;
      }

      const submissionData = {
        ...formData,
        registrationNumber: registrationValidation.normalizedValue
      };

      const result = await CompanyAccountService.createCompanyAccount(userId, submissionData);
      if (result.success && result.data) {
        const companyId = result.data.id;

        // Add additional officers (they will need to be invited/linked to user accounts later)
        // For now, we store them as pending officers
        for (const officer of additionalOfficers) {
          try {
            await CompanyAccountService.addPendingOfficer({
              companyId,
              firstName: officer.firstName,
              lastName: officer.lastName,
              email: officer.email,
              phone: officer.phone,
              role: officer.role,
              customRoleTitle: officer.customRoleTitle,
              shareholdingPercentage: officer.shareholdingPercentage
            });
          } catch (err) {
            console.error('Error adding officer:', err);
            // Continue with other officers even if one fails
          }
        }

        // Send registration confirmation email
        sendRegistrationConfirmationEmail({
          companyName: submissionData.legalName,
          registrationNumber: submissionData.registrationNumber,
          directorName: 'Director', // We don't have the director name in this form context
          directorEmail: submissionData.primaryEmail
        }).catch(err => console.error('Failed to send registration confirmation email:', err));

        onSuccess(companyId);
      } else {
        setSubmitError(result.error || 'Failed to register company');
      }
    } catch (error) {
      console.error('Error registering company:', error);
      setSubmitError('An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'company-info': {
        const isSouthAfricaRegistration = isSouthAfricaCompanyCountry(formData.jurisdictionCountryCode);
        return (
          <div className="space-y-4">
            <InputField label="Legal Name" field="legalName" value={getFieldValue('legalName')} onChange={updateField} required placeholder="e.g., Acme Holdings (Pty) Ltd" error={errors.legalName} maxLength={FIELD_LIMITS.companyName.max} />
            <InputField label="Trading Name" field="tradingName" value={getFieldValue('tradingName')} onChange={updateField} placeholder="e.g., Acme" error={errors.tradingName} maxLength={FIELD_LIMITS.companyName.max} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="Registration Country" field="jurisdictionCountryCode" value={getFieldValue('jurisdictionCountryCode')} onChange={updateField} options={companyCountryOptions} required error={errors.jurisdictionCountryCode} />
              <InputField label="Registration Number" field="registrationNumber" value={getFieldValue('registrationNumber')} onChange={updateField} required placeholder={isSouthAfricaRegistration ? '2005/123456/07' : 'e.g. BF BBD 2005B255'} error={errors.registrationNumber} maxLength={FIELD_LIMITS.companyRegNumber.max} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Tax Number" field="taxIdentificationNumber" value={getFieldValue('taxIdentificationNumber')} onChange={updateField} placeholder="e.g., 9012345678" error={errors.taxIdentificationNumber} />
              <SelectField label="Company Type" field="companyType" value={getFieldValue('companyType')} onChange={updateField} options={companyTypes} required error={errors.companyType} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Date of Incorporation" field="dateOfIncorporation" value={getFieldValue('dateOfIncorporation')} onChange={updateField} type="date" required error={errors.dateOfIncorporation} />
              <SelectField label="Industry" field="industryClassification" value={getFieldValue('industryClassification')} onChange={updateField} options={industries} error={errors.industryClassification} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Industry Detail" field="industryName" value={getFieldValue('industryName')} onChange={updateField} placeholder="Specific industry" error={errors.industryName} />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-300">Business Description</label>
              <textarea
                value={formData.businessDescription || ''}
                onChange={(e) => updateField('businessDescription', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description of business activities..."
              />
            </div>
          </div>
        );
      }

      case 'addresses':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-white font-medium mb-3">Registered Address</h4>
              <div className="space-y-4">
                <InputField label="Street Address" field="registeredAddress.street" value={getFieldValue('registeredAddress.street')} onChange={updateField} required placeholder="123 Main Street" error={errors['registeredAddress.street']} maxLength={FIELD_LIMITS.addressLine.max} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField label="City" field="registeredAddress.city" value={getFieldValue('registeredAddress.city')} onChange={updateField} required error={errors['registeredAddress.city']} maxLength={FIELD_LIMITS.city.max} />
                  <InputField label="Province/State" field="registeredAddress.province" value={getFieldValue('registeredAddress.province')} onChange={updateField} error={errors['registeredAddress.province']} maxLength={FIELD_LIMITS.province.max} />
                </div>
                <InputField label="Postal Code" field="registeredAddress.postalCode" value={getFieldValue('registeredAddress.postalCode')} onChange={updateField} required error={errors['registeredAddress.postalCode']} maxLength={FIELD_LIMITS.postalCode.max} />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="useBusinessAddress"
                checked={useBusinessAddress}
                onChange={(e) => setUseBusinessAddress(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
              />
              <label htmlFor="useBusinessAddress" className="text-gray-300 text-sm">
                Add a different business/operating address
              </label>
            </div>

            {useBusinessAddress && (
              <div>
                <h4 className="text-white font-medium mb-3">Business Address</h4>
                <div className="space-y-4">
                  <InputField label="Street Address" field="businessAddress.street" value={getFieldValue('businessAddress.street')} onChange={updateField} placeholder="456 Business Park" error={errors['businessAddress.street']} maxLength={FIELD_LIMITS.addressLine.max} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField label="City" field="businessAddress.city" value={getFieldValue('businessAddress.city')} onChange={updateField} error={errors['businessAddress.city']} maxLength={FIELD_LIMITS.city.max} />
                    <InputField label="Province/State" field="businessAddress.province" value={getFieldValue('businessAddress.province')} onChange={updateField} error={errors['businessAddress.province']} maxLength={FIELD_LIMITS.province.max} />
                  </div>
                  <InputField label="Postal Code" field="businessAddress.postalCode" value={getFieldValue('businessAddress.postalCode')} onChange={updateField} error={errors['businessAddress.postalCode']} maxLength={FIELD_LIMITS.postalCode.max} />
                </div>
              </div>
            )}
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-4">
            <InputField label="Primary Email" field="primaryEmail" value={getFieldValue('primaryEmail')} onChange={updateField} type="email" required placeholder="info@company.co.za" error={errors.primaryEmail} maxLength={FIELD_LIMITS.email.max} />
            <InputField label="Phone Number" field="primaryPhone" value={getFieldValue('primaryPhone')} onChange={updateField} placeholder="+27 11 123 4567" error={errors.primaryPhone} maxLength={FIELD_LIMITS.phone.max} />
            <InputField label="Website" field="websiteUrl" value={getFieldValue('websiteUrl')} onChange={updateField} placeholder="https://www.company.co.za" error={errors.websiteUrl} />
          </div>
        );

      case 'officers':
        return (
          <div className="space-y-6">
            <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
              <p className="text-blue-300 text-sm">
                <span className="font-medium">Note:</span> You are automatically registered as the Primary Director.
                Add additional officers below if needed, or skip this step.
              </p>
            </div>

            {/* List of added officers */}
            {additionalOfficers.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-white font-medium">Additional Officers</h4>
                {additionalOfficers.map((officer, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                    <div>
                      <div className="text-white font-medium">{officer.firstName} {officer.lastName}</div>
                      <div className="text-white text-sm">
                        {officerRoles.find(r => r.value === officer.role)?.label}
                        {officer.customRoleTitle && ` - ${officer.customRoleTitle}`}
                      </div>
                      <div className="text-white text-sm">{officer.email}</div>
                    </div>
                    <button
                      onClick={() => setAdditionalOfficers(prev => prev.filter((_, i) => i !== index))}
                      className="text-red-400 hover:text-red-300"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add officer form */}
            {showAddOfficer ? (
              <div className="border border-gray-600 rounded-lg p-4 space-y-4">
                <h4 className="text-white font-medium">Add Officer</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-300">First Name <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      value={newOfficer.firstName}
                      onChange={(e) => setNewOfficer(prev => ({ ...prev, firstName: e.target.value }))}
                      maxLength={FIELD_LIMITS.firstName.max}
                      className={`w-full px-4 py-2 bg-gray-700 border ${errors['officer.firstName'] ? 'border-red-500' : 'border-gray-600'} rounded-lg text-white`}
                    />
                    {errors['officer.firstName'] && <p className="text-red-400 text-sm">{errors['officer.firstName']}</p>}
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-300">Last Name <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      value={newOfficer.lastName}
                      onChange={(e) => setNewOfficer(prev => ({ ...prev, lastName: e.target.value }))}
                      maxLength={FIELD_LIMITS.lastName.max}
                      className={`w-full px-4 py-2 bg-gray-700 border ${errors['officer.lastName'] ? 'border-red-500' : 'border-gray-600'} rounded-lg text-white`}
                    />
                    {errors['officer.lastName'] && <p className="text-red-400 text-sm">{errors['officer.lastName']}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-300">Email <span className="text-red-400">*</span></label>
                    <input
                      type="email"
                      value={newOfficer.email}
                      onChange={(e) => setNewOfficer(prev => ({ ...prev, email: e.target.value }))}
                      maxLength={FIELD_LIMITS.email.max}
                      className={`w-full px-4 py-2 bg-gray-700 border ${errors['officer.email'] ? 'border-red-500' : 'border-gray-600'} rounded-lg text-white`}
                    />
                    {errors['officer.email'] && <p className="text-red-400 text-sm">{errors['officer.email']}</p>}
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-300">Phone</label>
                    <input
                      type="text"
                      value={newOfficer.phone}
                      onChange={(e) => setNewOfficer(prev => ({ ...prev, phone: e.target.value }))}
                      maxLength={FIELD_LIMITS.phone.max}
                      className={`w-full px-4 py-2 bg-gray-700 border ${errors['officer.phone'] ? 'border-red-500' : 'border-gray-600'} rounded-lg text-white`}
                    />
                    {errors['officer.phone'] && <p className="text-red-400 text-sm">{errors['officer.phone']}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-300">Role <span className="text-red-400">*</span></label>
                    <select
                      value={newOfficer.role}
                      onChange={(e) => setNewOfficer(prev => ({ ...prev, role: e.target.value as CompanyOfficerRole }))}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    >
                      {officerRoles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  {newOfficer.role === 'custom' && (
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-300">Custom Role Title</label>
                      <input
                        type="text"
                        value={newOfficer.customRoleTitle || ''}
                        onChange={(e) => setNewOfficer(prev => ({ ...prev, customRoleTitle: e.target.value }))}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-300">Shareholding %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={newOfficer.shareholdingPercentage}
                    onChange={(e) => setNewOfficer(prev => ({ ...prev, shareholdingPercentage: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowAddOfficer(false);
                      setNewOfficer({ ...emptyOfficer });
                    }}
                    className="px-4 py-2 text-gray-300 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (newOfficer.firstName && newOfficer.lastName && newOfficer.email) {
                        const officerErrors: Record<string, string> = {};
                        const fnResult = validateNameField(newOfficer.firstName, 'First name');
                        if (!fnResult.valid) officerErrors['officer.firstName'] = fnResult.error!;
                        const lnResult = validateNameField(newOfficer.lastName, 'Last name');
                        if (!lnResult.valid) officerErrors['officer.lastName'] = lnResult.error!;
                        const emResult = validateStrictEmail(newOfficer.email);
                        if (!emResult.valid) officerErrors['officer.email'] = emResult.error!;
                        if (newOfficer.phone && newOfficer.phone.trim()) {
                          const phResult = validatePhoneField(newOfficer.phone);
                          if (!phResult.valid) officerErrors['officer.phone'] = phResult.error!;
                        }
                        if (Object.keys(officerErrors).length > 0) {
                          setErrors(prev => ({ ...prev, ...officerErrors }));
                          return;
                        }
                        setAdditionalOfficers(prev => [...prev, { ...newOfficer }]);
                        setNewOfficer({ ...emptyOfficer });
                        setShowAddOfficer(false);
                      }
                    }}
                    disabled={!newOfficer.firstName || !newOfficer.lastName || !newOfficer.email}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Officer
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddOfficer(true)}
                className="w-full py-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-gray-500 hover:text-gray-300 transition-colors flex items-center justify-center space-x-2"
              >
                <span>+</span>
                <span>Add Another Officer</span>
              </button>
            )}
          </div>
        );

      case 'review':
        return (
          <div className="space-y-6">
            <div className="bg-gray-700/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-2">Company Information</h4>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <dt className="text-white">Legal Name:</dt>
                <dd className="text-white">{formData.legalName}</dd>
                <dt className="text-white">Registration:</dt>
                <dd className="text-white">{formData.registrationNumber}</dd>
                <dt className="text-white">Type:</dt>
                <dd className="text-white">{companyTypes.find(t => t.value === formData.companyType)?.label}</dd>
              </dl>
            </div>

            <div className="bg-gray-700/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-2">Registered Address</h4>
              <p className="text-gray-300 text-sm">
                {formData.registeredAddress.street}<br />
                {formData.registeredAddress.city}, {formData.registeredAddress.province} {formData.registeredAddress.postalCode}
              </p>
            </div>

            <div className="bg-gray-700/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-2">Contact</h4>
              <p className="text-gray-300 text-sm">{formData.primaryEmail}</p>
              {formData.primaryPhone && <p className="text-gray-300 text-sm">{formData.primaryPhone}</p>}
            </div>

            <div className="bg-gray-700/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-2">Officers</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white">You (Primary Director)</span>
                  <span className="text-green-400">✓ Auto-assigned</span>
                </div>
                {additionalOfficers.map((officer, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-white">{officer.firstName} {officer.lastName}</span>
                    <span className="text-white">{officerRoles.find(r => r.value === officer.role)?.label}</span>
                  </div>
                ))}
                {additionalOfficers.length === 0 && (
                  <p className="text-white text-sm">No additional officers added</p>
                )}
              </div>
            </div>

            {submitError && (
              <div className="bg-red-900/30 border border-red-500 rounded-lg p-4">
                <p className="text-red-400">{submitError}</p>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700">
      {/* Header */}
      <div className="border-b border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Register Company Account</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between mt-6">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === step.id ? 'bg-blue-600 text-white' :
                steps.findIndex(s => s.id === currentStep) > index ? 'bg-green-600 text-white' :
                'bg-gray-700 text-white'
              }`}>
                {steps.findIndex(s => s.id === currentStep) > index ? '✓' : index + 1}
              </div>
              <span className={`ml-2 text-sm ${currentStep === step.id ? 'text-white' : 'text-white'}`}>
                {step.label}
              </span>
              {index < steps.length - 1 && (
                <div className="w-12 h-0.5 mx-2 bg-gray-700"></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {renderStepContent()}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-700 p-6 flex justify-between">
        <button
          onClick={currentStep === 'company-info' ? onCancel : handleBack}
          className="px-6 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
        >
          {currentStep === 'company-info' ? 'Cancel' : 'Back'}
        </button>

        {currentStep === 'review' ? (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                <span>Registering...</span>
              </>
            ) : (
              <span>Register Company</span>
            )}
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Continue
          </button>
        )}
      </div>
    </div>
  );
};

export default CompanyRegistrationForm;

